import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronDown } from 'react-icons/fi';

const inputControlStyle = {
  minWidth: '140px',
  height: '34px',
  border: '1px solid #e2e8f0',
  borderRadius: '6px',
  padding: '0 12px',
  fontSize: '12px',
  background: '#fff',
  color: '#2d3748',
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: '#4a5568',
  fontWeight: 600,
  marginBottom: '4px',
};

const selectAllBtnStyle = {
  border: '1px solid #cbd5e0',
  background: '#f7fafc',
  color: '#2d3748',
  borderRadius: '6px',
  padding: '4px 10px',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  marginBottom: '8px',
};

// Generic checkbox multi-select dropdown for simple string options (status, priority, project, user id, etc.)
// Portal-rendered menu with viewport-aware open-up/open-down positioning.
const MultiSelectDropdown = ({
  label,
  placeholder,
  options,           // array of strings
  selectedValues,    // array of strings
  onChange,          // (newArray) => void
  getLabel,          // optional: opt => display label
  showSearch = false,     // optional: show search input
  showSelectAll = false,  // optional: show select all button
  showSelectAllCheckbox = false, // optional: show "Select All" checkbox row + "Remove All" action above the list
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuRect, setMenuRect] = useState(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const TOOLBAR_BORDER = '#dee2e6';
  const MENU_BORDER = '#334155';
  const MENU_SHADOW = '0 4px 16px rgba(15, 23, 42, 0.14), 0 1px 3px rgba(15, 23, 42, 0.08)';
  const ROW_PADDING = '6px 10px';
  const ROW_FONT_SIZE = 12;
  const HIGHLIGHT_BG = '#2563eb';
  const ROW_TEXT = '#0f172a';
  const MENU_VIEWPORT_GUTTER = 8;
  const MENU_TRIGGER_GAP = 4;
  const MENU_MIN_LIST_HEIGHT = 60;
  const MENU_ROW_ESTIMATE = 30;

  const selectedValuesRef = useRef(selectedValues);
  selectedValuesRef.current = selectedValues;

  const filteredOptions = useMemo(() => {
    const value = search.trim().toLowerCase();
    const list = !value
      ? options
      : options.filter((opt) => (getLabel ? getLabel(opt) : opt).toLowerCase().includes(value));
    const selectedSet = new Set((selectedValuesRef.current || []).map(String));
    const selected = list.filter((opt) => selectedSet.has(opt));
    const rest = list.filter((opt) => !selectedSet.has(opt));
    return [...selected, ...rest];
  }, [options, search, open]);

  const toggleOption = (opt) => {
    if (selectedValues.includes(opt)) {
      onChange(selectedValues.filter((v) => v !== opt));
    } else {
      onChange([...selectedValues, opt]);
    }
  };

  const filteredOptsArray = filteredOptions;
  const allSelected = filteredOptsArray.length > 0 && filteredOptsArray.every((o) => selectedValues.includes(o));
  const someSelected = !allSelected && filteredOptsArray.some((o) => selectedValues.includes(o));

  const selectAllCheckboxRef = useRef(null);
  useEffect(() => {
    if (selectAllCheckboxRef.current) selectAllCheckboxRef.current.indeterminate = someSelected;
  });

  const toggleSelectAll = () => {
    if (allSelected) {
      onChange(selectedValues.filter((v) => !filteredOptsArray.includes(v)));
    } else {
      onChange([...new Set([...selectedValues, ...filteredOptsArray])]);
    }
  };

  const displayLabel = (opt) => (getLabel ? getLabel(opt) : opt);

  const updateMenuRect = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = Math.max(0, viewportHeight - r.bottom - MENU_VIEWPORT_GUTTER - MENU_TRIGGER_GAP);
    const spaceAbove = Math.max(0, r.top - MENU_VIEWPORT_GUTTER - MENU_TRIGGER_GAP);
    const estimatedMenuHeight = Math.min(320, filteredOptsArray.length * MENU_ROW_ESTIMATE + 2);
    const shouldOpenUp = spaceBelow < Math.min(estimatedMenuHeight, 180) && spaceAbove > spaceBelow;
    const availableHeight = shouldOpenUp ? spaceAbove : spaceBelow;
    const listMaxHeight = Math.min(320, Math.max(MENU_MIN_LIST_HEIGHT, availableHeight || 320));
    const menuHeightForPosition = Math.min(listMaxHeight, estimatedMenuHeight);
    const top = shouldOpenUp
      ? Math.max(MENU_VIEWPORT_GUTTER, r.top - MENU_TRIGGER_GAP - menuHeightForPosition)
      : Math.max(MENU_VIEWPORT_GUTTER, r.bottom + MENU_TRIGGER_GAP);

    setMenuRect({
      left: r.left,
      top,
      width: r.width,
      listMaxHeight,
    });
  }, [filteredOptsArray.length]);

  useEffect(() => {
    if (!open) {
      setMenuRect(null);
      return;
    }
    updateMenuRect();
    const onScrollOrResize = () => updateMenuRect();
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);
    return () => {
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, updateMenuRect]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const triggerShellStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 8,
    paddingRight: 6,
    minHeight: 34,
    height: 34,
    width: '100%',
    border: `1px solid ${TOOLBAR_BORDER}`,
    borderRadius: 6,
    background: '#fff',
    boxSizing: 'border-box',
    minWidth: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
  };

  const labelStyle = {
    flex: 1,
    minWidth: 0,
    fontSize: 12,
    fontWeight: 500,
    color: '#344054',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const menuContent = open && menuRect ? (
    <div
      ref={menuRef}
      role="listbox"
      aria-label={placeholder}
      style={{
        position: 'fixed',
        left: menuRect.left,
        top: menuRect.top,
        width: menuRect.width,
        zIndex: 12000,
        background: '#fff',
        border: `1px solid ${MENU_BORDER}`,
        borderRadius: 4,
        boxShadow: MENU_SHADOW,
        overflow: 'hidden',
        boxSizing: 'border-box',
      }}
    >
      {(showSearch || showSelectAll) && (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', borderBottom: '1px solid #edf2f7' }}>
          {showSearch && (
            <input
              placeholder={`Search ${(label || placeholder || 'options').toLowerCase()}...`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              style={{ ...inputControlStyle, flex: 1, minWidth: 0, height: 30 }}
            />
          )}
          {showSelectAll && filteredOptsArray.length > 0 && (
            <button type="button" onClick={toggleSelectAll} style={{ ...selectAllBtnStyle, marginBottom: 0, whiteSpace: 'nowrap', flexShrink: 0, height: 30, padding: '0 10px' }}>
              {allSelected ? 'Deselect All' : 'Select All'}
            </button>
          )}
        </div>
      )}
      {showSelectAllCheckbox && filteredOptsArray.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: ROW_PADDING, borderBottom: '1px solid #edf2f7' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', minWidth: 0 }}>
            <input
              ref={selectAllCheckboxRef}
              type="checkbox"
              checked={allSelected}
              onChange={toggleSelectAll}
              style={{ width: '16px', height: '16px', flexShrink: 0, accentColor: HIGHLIGHT_BG }}
            />
            <span style={{ fontSize: ROW_FONT_SIZE, color: ROW_TEXT, fontWeight: 600 }}>Select All</span>
          </label>
          <button
            type="button"
            onClick={() => onChange([])}
            disabled={selectedValues.length === 0}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              fontSize: ROW_FONT_SIZE,
              fontWeight: 600,
              color: selectedValues.length === 0 ? '#a0aec0' : '#e53e3e',
              cursor: selectedValues.length === 0 ? 'default' : 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            Remove All
          </button>
        </div>
      )}
      <div style={{ maxHeight: menuRect.listMaxHeight ?? 320, overflowY: 'auto' }}>
        {filteredOptsArray.map((opt) => {
          const checked = selectedValues.includes(opt);
          return (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: ROW_PADDING,
                cursor: 'pointer',
                background: '#ffffff',
                borderBottom: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleOption(opt)}
                style={{ width: '16px', height: '16px', flexShrink: 0, accentColor: HIGHLIGHT_BG }}
              />
              <span style={{ fontSize: ROW_FONT_SIZE, color: ROW_TEXT, fontWeight: checked ? 600 : 400 }}>{displayLabel(opt)}</span>
            </label>
          );
        })}
      </div>
    </div>
  ) : null;

  return (
    <div style={{ position: 'relative', width: '100%', minWidth: 0 }}>
      {label && <div style={fieldLabelStyle}>{label}</div>}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={placeholder}
        aria-haspopup="listbox"
        aria-expanded={open}
        style={{
          ...triggerShellStyle,
          cursor: 'pointer',
        }}
      >
        <span style={labelStyle}>
          {selectedValues.length > 0
            ? selectedValues.map(displayLabel).join(', ')
            : (placeholder || 'Nothing selected')}
        </span>
        <FiChevronDown
          size={14}
          aria-hidden
          style={{
            flexShrink: 0,
            color: '#98a2b3',
            transform: open ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.15s ease',
          }}
        />
      </button>
      {typeof document !== 'undefined' && menuContent ? createPortal(menuContent, document.body) : null}
    </div>
  );
};

export default MultiSelectDropdown;
